import { FormEvent, useMemo, useRef } from 'react';
import { useClient } from '../../../hooks/useClient';
import { useForm } from '../../../hooks/useForm';
import { ReservationTypeWithColor, useReservationTypes } from '../../../hooks/AppData/useReservationTypes';
import { Branch } from '../../../types/branch';
import { getInitialReservationHoursForm, RESERVATION_HOURS_FORM_VALIDATORS, ReservationHoursForm } from '../validators';

interface Options {
  selectedBranchId: string | undefined;
  selectedBranch: Branch | undefined;
  branchReservationTypes: ReservationTypeWithColor[] | undefined;
  reservationTypes: ReservationTypeWithColor[] | undefined;
  mutateReservationTypes: ReturnType<typeof useReservationTypes>['mutateReservationTypes'];
  selectedReservationType: ReservationTypeWithColor | undefined;
}

export const useReservationHoursForm = ({
  selectedBranchId,
  selectedBranch,
  branchReservationTypes,
  reservationTypes,
  mutateReservationTypes,
  selectedReservationType,
}: Options) => {
  const { patch } = useClient();

  const reservationHoursFormRef = useRef(null);
  const initialReservationHoursForm = useMemo(
    () => getInitialReservationHoursForm(branchReservationTypes),
    [branchReservationTypes],
  );
  const {
    form: reservationHours,
    setFormFields: setReservationHoursField,
    isSaving: reservationHoursSaving,
    setIsSaving: setReservationHoursSaving,
    hasChanged: reservationHoursChanged,
    validate: reservationHoursValidate,
  } = useForm<ReservationHoursForm | undefined>(
    initialReservationHoursForm,
    false,
    RESERVATION_HOURS_FORM_VALIDATORS,
    reservationHoursFormRef,
  );

  const handleSaveReservationHours = async (e: FormEvent) => {
    e.preventDefault();
    // RESERVATION_HOURS_FORM_VALIDATORS has no validator: the hours are always valid.
    reservationHoursValidate();
    if (!reservationHours || !selectedBranchId || !selectedBranch || !branchReservationTypes) return;
    setReservationHoursSaving(true);

    const updatedReservationTypes = branchReservationTypes.map((reservationType) => {
      const { color, branchId, tagColor, createdAt, ...rest } = reservationType;
      return {
        ...rest,
        hours: reservationHours[reservationType.id].hours,
        closedDays: reservationHours[reservationType.id].closedDays,
      };
    });

    await patch(`/branches/${selectedBranchId}/reservation-types`, {
      body: { updatedReservationTypes },
      errorMessages: {
        summary: 'Error al cambiar horario',
        defaultDetail: 'No se ha podido modificar el horario de apertura',
      },
      successMessage: {
        summary: 'Horario editado',
        detail: 'El horario de apertura se ha modificado correctamente',
      },
      handlers: {
        defaultSuccess: () => {
          mutateReservationTypes();
        },
      },
    });

    setReservationHoursSaving(false);
  };

  const handleHolidaysChange = (dates: Date[]) => {
    if (!selectedReservationType) return;

    const currentHours = reservationHours?.[selectedReservationType.id]?.hours;
    if (!currentHours) return;

    setReservationHoursField({
      [selectedReservationType.id]: {
        hours: currentHours,
        closedDays: dates,
      },
    });
  };

  const handleRemoveHoliday = (reservationType: ReservationTypeWithColor, dateToRemove: Date) => {
    const currentHours = reservationHours?.[reservationType.id]?.hours;
    const currentHolidays = reservationHours?.[reservationType.id]?.closedDays ?? [];
    if (!currentHours) return;

    setReservationHoursField({
      [reservationType.id]: {
        hours: currentHours,
        closedDays: currentHolidays.filter((date) => date.toISOString() !== dateToRemove.toISOString()),
      },
    });
  };

  const handleCopyHours = (
    sourceBranchId: string,
    options: { copyReservationHours: boolean; copyClosedDays: boolean; copyBreakHours: boolean },
  ) => {
    if (!selectedBranchId || !sourceBranchId || !reservationTypes) return;

    const sourceBranchReservationTypes = reservationTypes.filter((type) => type.branchId === sourceBranchId);
    const targetBranchReservationTypes = reservationTypes.filter((type) => type.branchId === selectedBranchId);

    // Copiar horarios de reserva y días de cierre
    if (options.copyReservationHours || options.copyClosedDays) {
      const newReservationHours: ReservationHoursForm = {};

      targetBranchReservationTypes.forEach((targetType) => {
        const sourceType = sourceBranchReservationTypes.find((sourceType) => sourceType.name === targetType.name);

        if (sourceType) {
          newReservationHours[targetType.id] = {
            hours: options.copyReservationHours
              ? sourceType.hours
              : (reservationHours?.[targetType.id]?.hours ?? targetType.hours),
            closedDays: options.copyClosedDays
              ? (reservationHours?.[sourceType.id]?.closedDays ?? sourceType.closedDays.map((date) => new Date(date)))
              : (reservationHours?.[targetType.id]?.closedDays ?? []),
          };
        }
      });

      setReservationHoursField(newReservationHours);
    }
  };

  return {
    formRef: reservationHoursFormRef,
    form: reservationHours,
    setFormFields: setReservationHoursField,
    isSaving: reservationHoursSaving,
    hasChanged: reservationHoursChanged,
    handleSave: handleSaveReservationHours,
    handleHolidaysChange,
    handleRemoveHoliday,
    handleCopyHours,
  };
};

export type ReservationHoursFormState = ReturnType<typeof useReservationHoursForm>;
